import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { Map } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import { transformReduxDataToGeoJSON } from "../../utils/dataTransformer";
import LegendGroup from "../LegendGroup";
import BaseMap from "../BaseMap";
import Peel1937 from "../../assets/allThePoints/baseMap/Peel_1937.json";
import AreaGazaStrip1997 from "../../assets/allThePoints/baseMap/Area_Gaza_Strip_1997.geojson";
import UNPartition1947 from "../../assets/allThePoints/baseMap/UN_Partition_1947.json";
import { getAllThePointsAction, updateAllThePointsAction } from "../../Redux/Action/newfile-action";
import { useDispatch, useSelector } from "react-redux";
import { rangeStyles, playPauseStyles, speedControlStyles } from "./ATPStyles";
import ATPHeading from '../../assets/allThePoints/ATP-heading.png';
import settlementTypes from "../../assets/allThePoints/settlementTypes.json";

const MAPBOX_TOKEN =
  "pk.eyJ1IjoiZ2VvdGhhcmEiLCJhIjoiY2xrd21tb2VpMTJ0djNybXliZzhwb29hOCJ9.3hVMlLmgoVu8cOPEH5IsYg";

export default function App() {
  const dispatch = useDispatch();

  const mapRef = useRef(null);
  const MIN_YEAR = 1839;
  const MAX_YEAR = 2023;
  const INITIAL_POINT1 = 1840;
  const INITIAL_POINT2 = 1845;

  const [point1, setPoint1] = useState(INITIAL_POINT1);
  const [point2, setPoint2] = useState(INITIAL_POINT2);
  const [unlimitedZoom, setUnlimitedZoom] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [fps, setFps] = useState(10); // Add FPS state
  const animationRef = useRef(null);
  
  // Get data from Redux store
  const { allThePoints } = useSelector(
    (state) => state.getAllThePointsReducer
  );

  // Function to derive community groups from API data using settlementTypes.json
  const deriveCommunityGroups = useCallback((allThePoints) => {
    if (!allThePoints || !Array.isArray(allThePoints)) {
      return {};
    }

    const groups = {};
    
    allThePoints.forEach(point => {
      const settlementClass = point.SettlementClassification || "";
      if (settlementClass.trim()) {
        const settlementType = settlementTypes[settlementClass];
        
        if (settlementType) {
          const groupName = settlementType.group;
          
          if (!groups[groupName]) {
            groups[groupName] = {
              name: groupName,
              color: getColorForGroup(settlementType.color),
              shape: settlementType.shape,
              icon: settlementType.icon,
              codes: new Set()
            };
          }
          
          groups[groupName].codes.add(settlementClass);
        }
      }
    });

    // Convert Sets to arrays
    Object.keys(groups).forEach(groupName => {
      groups[groupName].codes = Array.from(groups[groupName].codes);
    });

    return groups;
  }, []);

  // Generate a color based on settlement type color
  const getColorForGroup = (colorName) => {
    const colorMapping = {
      green: "#48752C",
      maroon: "#7A2917", 
      purple: "#8C1AF5",
      pink: "#EA33F7",
      red: "#B83F29",
      black: "#000000",
      orange: "#F19E38",
      yellow: "#FFFF54",
      blue: "#5885E1",
      blue_green: "#4ECDC4",
      dark_blue: "#45B7D1",
      grey: "#CCCCCC",
      red_black: "#8B0000"
    };
    
    return colorMapping[colorName] || "#CCCCCC";
  };



  // Derive community groups from API data
  const communityGroups = useMemo(() => deriveCommunityGroups(allThePoints), [allThePoints, deriveCommunityGroups]);

  // Add loading state
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);

  const [checkedGroups, setCheckedGroups] = useState({});
  const [openGroups, setOpenGroups] = useState({});
  const [allSelected, setAllSelected] = useState(true);
  const [allExpanded, setAllExpanded] = useState(true);
  const [advancedMode, setAdvancedMode] = useState(true);
  const [iconImports, setIconImports] = useState({});
  const [rightCheckedGroups, setRightCheckedGroups] = useState({});
  const [rightOpenGroups, setRightOpenGroups] = useState({});
  const [rightAllSelected, setRightAllSelected] = useState(false);

  // Timeline label overlap detection
  const point1LabelRef = useRef(null);
  const point2LabelRef = useRef(null);
  const [labelsOverlap, setLabelsOverlap] = useState(false);

  // Loop range state
  const [loopStart, setLoopStart] = useState(MIN_YEAR);
  const [loopEnd, setLoopEnd] = useState(MAX_YEAR);

  // Add state for legend visibility
  const [showLeftLegend, setShowLeftLegend] = useState(true);
  const [showRightLegend, setShowRightLegend] = useState(true);
  
  // Add ref to track current state for event handlers
  const showRightLegendRef = useRef(true);
  
  // Update ref when state changes
  useEffect(() => {
    showRightLegendRef.current = showRightLegend;
  }, [showRightLegend]);

  // Add state for collapsible sections (place near other useState calls)
  const [isInfoPanelCollapsed, setIsInfoPanelCollapsed] = useState(false);

  useEffect(() => {
    dispatch(getAllThePointsAction());
  }, [dispatch]);

  // Handle data loading states
  useEffect(() => {
    if (allThePoints) {
      setIsDataLoading(false);
      setDataError(null);
    }
  }, [allThePoints]);

  // Initialize checked and open groups when community groups change
  useEffect(() => {
    if (Object.keys(communityGroups).length > 0) {
      const initialCheckedGroups = {};
      const initialOpenGroups = {};
      Object.keys(communityGroups).forEach((code) => {
        initialCheckedGroups[code] = true;
        initialOpenGroups[code] = true;
      });
      setCheckedGroups(initialCheckedGroups);
      setOpenGroups(initialOpenGroups);
    }
  }, [communityGroups]);

  // Transform Redux data to GeoJSON format
  const enrichedGeojson = useMemo(() => {
    return transformReduxDataToGeoJSON(allThePoints);
  }, [allThePoints]);

  // Update map data when Redux data changes
  useEffect(() => {
    if (mapRef.current && enrichedGeojson.features.length > 0) {
      const map = mapRef.current;
      const source = map.getSource("points");
      if (source) {
        source.setData(enrichedGeojson);
      }
    }
  }, [enrichedGeojson]);

  useEffect(() => {
    const iconContext = require.context(
      "../../assets/allThePoints/legend",
      false,
      /\.png$/
    );
    const imports = iconContext.keys().reduce((acc, key) => {
      const name = key.replace("./", "");
      acc[name] = iconContext(key);
      return acc;
    }, {});
    setIconImports(imports);
  }, []);

  useEffect(() => {
    if (point1LabelRef.current && point2LabelRef.current) {
      const rect1 = point1LabelRef.current.getBoundingClientRect();
      const rect2 = point2LabelRef.current.getBoundingClientRect();
      // Check horizontal overlap
      const overlap = !(rect1.right < rect2.left || rect1.left > rect2.right);
      setLabelsOverlap(overlap);
    }
  }, [point1, point2]);

  const handleCheck = (group, isChecked) => {
    if (!isChecked) {
      setAllSelected(false);
    }
    setCheckedGroups((prev) => ({
      ...prev,
      [group]: isChecked,
    }));
  };

  const handleOpen = (group) => {
    if (!openGroups[group]) {
      setAllExpanded(false);
    }
    setOpenGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  const handleSelectAll = () => {
    const newCheckedGroups = {};
    if (allSelected) {
      Object.keys(communityGroups).forEach((code) => {
        newCheckedGroups[code] = false;
      });
      setAllSelected(false);
    } else {
      Object.keys(communityGroups).forEach((code) => {
        newCheckedGroups[code] = true;
      });
      setAllSelected(true);
    }
    setCheckedGroups(newCheckedGroups);
  };

  const handleModeChange = () => {
    setAdvancedMode(!advancedMode);
  };

  const handleExpandAll = () => {
    const newOpenGroups = {};
    if (allExpanded) {
      Object.keys(communityGroups).forEach((code) => {
        newOpenGroups[code] = false;
      });
      setAllExpanded(false);
    } else {
      Object.keys(communityGroups).forEach((code) => {
        newOpenGroups[code] = true;
      });
      setAllExpanded(true);
    }
    setOpenGroups(newOpenGroups);
  };

  /** Filter the map (year range, checked groups) */
  const combinedFilter = useMemo(() => {
    const checkedGroupNames = Object.entries(checkedGroups)
      .filter(([, v]) => v)
      .map(([k]) => k);

    // "no groups" case:
    if (!checkedGroupNames.length) {
      return ["==", "groupClassification", "__none__"];
    }

    // Get all settlement classifications that belong to checked groups
    const checkedSettlementClasses = [];
    checkedGroupNames.forEach(groupName => {
      const group = communityGroups[groupName];
      if (group && group.codes) {
        checkedSettlementClasses.push(...group.codes);
      }
    });

    return [
      "all",
      ["in", "SettlementClassification", ...checkedSettlementClasses],
      ["<=", "creationYear", Math.max(point1, point2)],
      [">=", "endYear", Math.min(point1, point2)],
    ];
  }, [checkedGroups, point1, point2, communityGroups]);

  useEffect(() => {
    if (!mapRef.current) return;
    console.log("combinedFilter", combinedFilter);
    
    const layer = mapRef.current.getLayer("point-symbols");
    if (layer) {
      mapRef.current.setFilter("point-symbols", combinedFilter);
    }
  }, [combinedFilter]);

  useEffect(() => {
    if (!mapRef.current) return;

    const layer = mapRef.current.getLayer("point-symbols");
    if (!layer) return;

    // Update the filter based on advanced mode
    const currentFilter = mapRef.current.getFilter("point-symbols");
    if (currentFilter && currentFilter[0] === "all") {
      let newFilter;
      
      if (advancedMode) {
        // Show all shapes - keep the current filter but ensure all shapes are included
        newFilter = currentFilter;
      } else {
        // Only show group-level shapes (first 6 shapes)
        const groupShapes = ['circle', 'square', 'triangle', 'diamond', 'star', 'hexagon'];
        const shapeFilter = ["in", "shape", ...groupShapes];
        
        // Replace the existing filter conditions but keep the year and group filters
        const yearAndGroupFilters = currentFilter.slice(1).filter(condition => 
          !(Array.isArray(condition) && condition[0] === "in" && condition[1] === "shape")
        );
        
        newFilter = ["all", shapeFilter, ...yearAndGroupFilters];
      }
      
      mapRef.current.setFilter("point-symbols", newFilter);
    }
  }, [advancedMode]);

  useEffect(() => {
    if (mapRef.current) {
      if (unlimitedZoom) {
        mapRef.current.setMinZoom(0);
        mapRef.current.setMaxZoom(24);
      } else {
        mapRef.current.setMinZoom(5);
        mapRef.current.setMaxZoom(18);
      }
    }
  }, [unlimitedZoom]);



  const handleTrackDrag = (e) => {
    const startX = e.clientX;
    const startPoint1 = point1;
    const startPoint2 = point2;
    const container = e.target.parentElement;
    const rect = container.getBoundingClientRect();
    const trackWidth = rect.width - 24; // Account for padding
    const yearRange = MAX_YEAR - MIN_YEAR;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const deltaYears = Math.round((deltaX / trackWidth) * yearRange);
      let newPoint1 = startPoint1 + deltaYears;
      let newPoint2 = startPoint2 + deltaYears;
      // Clamp so both points stay valid
      // 1. point1 >= loopStart + 1
      // 2. point2 <= loopEnd - 1
      if (newPoint1 < loopStart + 1) {
        newPoint2 += loopStart + 1 - newPoint1;
        newPoint1 = loopStart + 1;
      }
      if (newPoint2 > loopEnd - 1) {
        newPoint1 -= newPoint2 - (loopEnd - 1);
        newPoint2 = loopEnd - 1;
      }
      // Allow points to be equal
      if (newPoint2 < newPoint1) {
        newPoint2 = newPoint1;
      }
      // Final clamp to valid range
      newPoint1 = Math.max(loopStart + 1, Math.min(newPoint1, loopEnd - 1));
      newPoint2 = Math.max(loopStart + 1, Math.min(newPoint2, loopEnd - 1));
      setPoint1(newPoint1);
      setPoint2(newPoint2);
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      // Clamp to valid range
      setPoint1((prev) => Math.max(loopStart + 1, Math.min(prev, loopEnd - 1)));
      setPoint2((prev) => Math.max(loopStart + 1, Math.min(prev, loopEnd - 1)));
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };
  // END timeline handlers



  const handleMapLoad = (e) => {
    const map = e.target;
    mapRef.current = map;

    const layers = map.getStyle().layers;
    const firstSymbolLayer = layers.find((layer) => layer.type === "symbol");

    if (firstSymbolLayer) {
      // Add 3D buildings
      map.addLayer(
        {
          id: "3d-buildings",
          source: "composite",
          "source-layer": "building",
          filter: ["==", "extrude", "true"],
          type: "fill-extrusion",
          minzoom: 15,
          paint: {
            "fill-extrusion-color": "#aaa",
            "fill-extrusion-height": [
              "interpolate",
              ["linear"],
              ["zoom"],
              15,
              0,
              15.05,
              ["get", "height"],
            ],
            "fill-extrusion-base": [
              "interpolate",
              ["linear"],
              15,
              0,
              15.05,
              ["get", "min_height"],
            ],
            "fill-extrusion-opacity": 0.6,
          },
        },
        firstSymbolLayer.id
      );

      map.addSource("points", {
        type: "geojson",
        data: enrichedGeojson,
      });

      // Create custom markers for each shape and color combination
      const shapes = ['circle', 'square', 'triangle', 'diamond', 'star', 'hexagon', 'cross', 'plus'];
      const colors = [
        '#48752C', '#7A2917', '#8C1AF5', '#EA33F7', '#B83F29', 
        '#000000', '#F19E38', '#FFFF54', '#5885E1', '#CCCCCC',
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
        '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'
      ];
      
      // Create markers for each shape-color combination
      shapes.forEach((shape) => {
        colors.forEach((color) => {
          const markerId = `shape-${shape}-${color.replace('#', '')}`;
          
          // Create SVG for this shape and color
          const svg = createShapeSVG(shape, 24, color);
          
          // Convert SVG to data URL
          const svgString = new XMLSerializer().serializeToString(svg);
          const dataURL = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgString);
          
          // Create image element and add to map
          const img = new Image();
          img.onload = () => {
            if (!map.hasImage(markerId)) {
              map.addImage(markerId, img);
            }
          };
          img.src = dataURL;
        });
      });

      // Add symbol layer for all shapes
      map.addLayer({
        id: "point-symbols",
        type: "symbol",
        source: "points",
        layout: {
          "icon-image": [
            "concat", 
            "shape-", 
            ["get", "shape"], 
            "-", 
            ["slice", ["get", "color"], 1]
          ],
          "icon-size": [
            "interpolate",
            ["linear"],
            ["zoom"],
            5, 0.5,
            10, 1,
            15, 1.5
          ],
          "icon-allow-overlap": true,
        },
      });

      // Set initial timeline values and apply filter immediately
      setPoint1(INITIAL_POINT1);
      setPoint2(INITIAL_POINT2);

      // Apply the initial filter immediately after layer creation
      const checkedGroupNames = Object.keys(communityGroups);
      const checkedSettlementClasses = [];
      checkedGroupNames.forEach(groupName => {
        const group = communityGroups[groupName];
        if (group && group.codes) {
          checkedSettlementClasses.push(...group.codes);
        }
      });
      
      const initialFilter = [
        "all",
        ["in", "SettlementClassification", ...checkedSettlementClasses],
        ["<=", "creationYear", INITIAL_POINT2],
        [">=", "endYear", INITIAL_POINT1],
      ];
      
      map.setFilter("point-symbols", initialFilter);

      // Layer to highlight the selected point
      map.addSource("selected-point", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      map.addLayer({
        id: "selected-point-circle",
        type: "circle",
        source: "selected-point",
        paint: {
          "circle-radius": 8,
          "circle-color": "transparent", // fill is fully transparent
          "circle-stroke-width": 2,
          "circle-stroke-color": "#00ffff", // cyan outline
        },
      });

      // Handle clicks on the symbol layer
      map.on("click", "point-symbols", (e) => {
        const clickedFeature = e.features[0];
        const clickedPoint = {
          type: "FeatureCollection",
          features: [clickedFeature],
        };

        map.getSource("selected-point").setData(clickedPoint);
        setSelectedPoint(clickedFeature.properties);
        
        // Open right sidebar if it's not already open
        if (!showRightLegendRef.current) {
          setShowRightLegend(true);
        }
      });

      // Clear selected point when clicking on the map background
      map.on("click", (e) => {
        const features = map.queryRenderedFeatures(e.point, {
          layers: ["point-symbols"],
        });
        if (!features.length) {
          map.getSource("selected-point").setData({
            type: "FeatureCollection",
            features: [],
          });
          setSelectedPoint(null);
        }
      });
    }
  };

  // Function to create SVG shapes for map markers
  const createShapeSVG = (shape, size = 24, color = "#CCCCCC") => {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("width", size);
    svg.setAttribute("height", size);
    svg.setAttribute("viewBox", `0 0 ${size} ${size}`);
    
    const halfSize = size / 2;
    
    switch (shape) {
      case 'circle':
        const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
        circle.setAttribute("cx", halfSize);
        circle.setAttribute("cy", halfSize);
        circle.setAttribute("r", halfSize - 1);
        circle.setAttribute("fill", color);
        circle.setAttribute("stroke", "#ffffff");
        circle.setAttribute("stroke-width", "0.5");
        svg.appendChild(circle);
        break;
        
      case 'square':
        const square = document.createElementNS("http://www.w3.org/2000/svg", "rect");
        square.setAttribute("x", 1);
        square.setAttribute("y", 1);
        square.setAttribute("width", size - 2);
        square.setAttribute("height", size - 2);
        square.setAttribute("fill", color);
        square.setAttribute("stroke", "#ffffff");
        square.setAttribute("stroke-width", "0.5");
        svg.appendChild(square);
        break;
        
      case 'triangle':
        const triangle = document.createElementNS("http://www.w3.org/2000/svg", "polygon");
        triangle.setAttribute("points", `${halfSize},1 ${size - 1},${size - 1} 1,${size - 1}`);
        triangle.setAttribute("fill", color);
        triangle.setAttribute("stroke", "#ffffff");
        triangle.setAttribute("stroke-width", "0.5");
        svg.appendChild(triangle);
        break;
        
      case 'diamond':
        const diamond = document.createElementNS("http://www.w3.org/2000/svg", "polygon");
        diamond.setAttribute("points", `${halfSize},1 ${size - 1},${halfSize} ${halfSize},${size - 1} 1,${halfSize}`);
        diamond.setAttribute("fill", color);
        diamond.setAttribute("stroke", "#ffffff");
        diamond.setAttribute("stroke-width", "0.5");
        svg.appendChild(diamond);
        break;
        
      case 'star':
        const star = document.createElementNS("http://www.w3.org/2000/svg", "polygon");
        star.setAttribute("points", `${halfSize},1 ${halfSize + 2},${halfSize - 2} ${size - 1},${halfSize} ${halfSize + 2},${halfSize + 2} ${halfSize},${size - 1} ${halfSize - 2},${halfSize + 2} 1,${halfSize} ${halfSize - 2},${halfSize - 2}`);
        star.setAttribute("fill", color);
        star.setAttribute("stroke", "#ffffff");
        star.setAttribute("stroke-width", "0.5");
        svg.appendChild(star);
        break;
        
      case 'hexagon':
        const hexagon = document.createElementNS("http://www.w3.org/2000/svg", "polygon");
        hexagon.setAttribute("points", `${halfSize},1 ${size - 2},${halfSize - 3} ${size - 2},${halfSize + 3} ${halfSize},${size - 1} 2,${halfSize + 3} 2,${halfSize - 3}`);
        hexagon.setAttribute("fill", color);
        hexagon.setAttribute("stroke", "#ffffff");
        hexagon.setAttribute("stroke-width", "0.5");
        svg.appendChild(hexagon);
        break;
        
      case 'cross':
        const cross1 = document.createElementNS("http://www.w3.org/2000/svg", "rect");
        cross1.setAttribute("x", halfSize - 1);
        cross1.setAttribute("y", 2);
        cross1.setAttribute("width", 2);
        cross1.setAttribute("height", size - 4);
        cross1.setAttribute("fill", color);
        cross1.setAttribute("stroke", "#ffffff");
        cross1.setAttribute("stroke-width", "0.5");
        svg.appendChild(cross1);
        
        const cross2 = document.createElementNS("http://www.w3.org/2000/svg", "rect");
        cross2.setAttribute("x", 2);
        cross2.setAttribute("y", halfSize - 1);
        cross2.setAttribute("width", size - 4);
        cross2.setAttribute("height", 2);
        cross2.setAttribute("fill", color);
        cross2.setAttribute("stroke", "#ffffff");
        cross2.setAttribute("stroke-width", "0.5");
        svg.appendChild(cross2);
        break;
        
      case 'plus':
        const plus1 = document.createElementNS("http://www.w3.org/2000/svg", "rect");
        plus1.setAttribute("x", halfSize - 1);
        plus1.setAttribute("y", 1);
        plus1.setAttribute("width", 2);
        plus1.setAttribute("height", size - 2);
        plus1.setAttribute("fill", color);
        svg.appendChild(plus1);
        
        const plus2 = document.createElementNS("http://www.w3.org/2000/svg", "rect");
        plus2.setAttribute("x", 1);
        plus2.setAttribute("y", halfSize - 1);
        plus2.setAttribute("width", size - 2);
        plus2.setAttribute("height", 2);
        plus2.setAttribute("fill", color);
        svg.appendChild(plus2);
        break;
        
      default:
        const defaultCircle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
        defaultCircle.setAttribute("cx", halfSize);
        defaultCircle.setAttribute("cy", halfSize);
        defaultCircle.setAttribute("r", halfSize - 1);
        defaultCircle.setAttribute("fill", color);
        defaultCircle.setAttribute("stroke", "#ffffff");
        defaultCircle.setAttribute("stroke-width", "0.5");
        svg.appendChild(defaultCircle);
    }
    
    return svg;
  };

  const startAnimation = () => {
    if (animationRef.current) return;

    setIsPlaying(true);
    const startTime = Date.now();
    // Use loopStart/loopEnd for animation bounds
    const initialPoint1 = loopStart;
    const initialPoint2 = loopStart + (point2 - point1);
    const initialGap = point2 - point1; // Store the initial gap between points
    let lastFrameTime = startTime;
    let yearsElapsed = 0;

    const animate = () => {
      const currentTime = Date.now();
      const frameInterval = 1000 / fps; // Calculate frame interval based on FPS
      if (currentTime - lastFrameTime >= frameInterval) {
        yearsElapsed += 1;
        lastFrameTime = currentTime;
      }
      const newPoint2 = Math.min(loopEnd, initialPoint2 + yearsElapsed);
      const newPoint1 = Math.min(loopEnd - initialGap, newPoint2 - initialGap);

      if (newPoint2 >= loopEnd) {
        // Reset to initial positions instead of stopping
        setPoint1(initialPoint1);
        setPoint2(initialPoint2);
        yearsElapsed = 0; // Reset the elapsed years
      } else {
        setPoint1(newPoint1);
        setPoint2(newPoint2);
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
  };

  const stopAnimation = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    setIsPlaying(false);
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      stopAnimation();
    } else {
      startAnimation();
    }
  };

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // Initialize right legend state
  useEffect(() => {
    const newCheckedGroups = {};
    Object.keys(communityGroups).forEach((code) => {
      newCheckedGroups[code] = false;
    });
    setRightCheckedGroups(newCheckedGroups);
    setRightAllSelected(false);

    const newOpenGroups = {};
    Object.keys(communityGroups).forEach((code) => {
      newOpenGroups[code] = true;
    });
    setRightOpenGroups(newOpenGroups);
  }, [communityGroups]);

  // Right legend handlers
  const handleRightCheck = (group, isChecked) => {
    if (!isChecked) {
      setRightAllSelected(false);
    }
    setRightCheckedGroups((prev) => ({
      ...prev,
      [group]: isChecked,
    }));

    // Special logic for Peel Commission 1937 (group 'A')
    if (group === "A" && mapRef.current) {
      const map = mapRef.current;
      if (!isChecked) {
        if (map.getLayer("peel-1937-fill")) map.removeLayer("peel-1937-fill");
        if (map.getLayer("peel-1937-line")) map.removeLayer("peel-1937-line");
        if (map.getSource("peel-1937")) map.removeSource("peel-1937");
      } else {
        if (!map.getSource("peel-1937")) {
          map.addSource("peel-1937", {
            type: "geojson",
            data: Peel1937,
          });
        }
        if (!map.getLayer("peel-1937-fill")) {
          map.addLayer({
            id: "peel-1937-fill",
            type: "fill",
            source: "peel-1937",
            paint: {
              "fill-color": "#48752C",
              "fill-opacity": 0.15,
            },
          });
        }
        if (!map.getLayer("peel-1937-line")) {
          map.addLayer({
            id: "peel-1937-line",
            type: "line",
            source: "peel-1937",
            paint: {
              "line-color": "#48752C",
              "line-width": 2,
            },
          });
        }
      }
    }

    // Special logic for UN Partition 1947 (group 'C')
    if (group === "C" && mapRef.current) {
      const map = mapRef.current;
      if (!isChecked) {
        if (map.getLayer("un-partition-1947-fill"))
          map.removeLayer("un-partition-1947-fill");
        if (map.getLayer("un-partition-1947-line"))
          map.removeLayer("un-partition-1947-line");
        if (map.getSource("un-partition-1947"))
          map.removeSource("un-partition-1947");
      } else {
        if (!map.getSource("un-partition-1947")) {
          map.addSource("un-partition-1947", {
            type: "geojson",
            data: UNPartition1947,
          });
        }
        if (!map.getLayer("un-partition-1947-fill")) {
          map.addLayer({
            id: "un-partition-1947-fill",
            type: "fill",
            source: "un-partition-1947",
            paint: {
              "fill-color": "#0066CC",
              "fill-opacity": 0.15,
            },
          });
        }
        if (!map.getLayer("un-partition-1947-line")) {
          map.addLayer({
            id: "un-partition-1947-line",
            type: "line",
            source: "un-partition-1947",
            paint: {
              "line-color": "#0066CC",
              "line-width": 2,
            },
          });
        }
      }
    }

    // Special logic for Area Gaza Strip 1997 (group 'B')
    if (group === "B" && mapRef.current) {
      const map = mapRef.current;
      if (!isChecked) {
        if (map.getLayer("gaza-1997-fill")) map.removeLayer("gaza-1997-fill");
        if (map.getLayer("gaza-1997-line")) map.removeLayer("gaza-1997-line");
        if (map.getSource("gaza-1997")) map.removeSource("gaza-1997");
      } else {
        if (!map.getSource("gaza-1997")) {
          map.addSource("gaza-1997", {
            type: "geojson",
            data: AreaGazaStrip1997,
          });
        }
        if (!map.getLayer("gaza-1997-fill")) {
          map.addLayer({
            id: "gaza-1997-fill",
            type: "fill",
            source: "gaza-1997",
            paint: {
              "fill-color": "#00bfff",
              "fill-opacity": 0.15,
            },
          });
        }
        if (!map.getLayer("gaza-1997-line")) {
          map.addLayer({
            id: "gaza-1997-line",
            type: "line",
            source: "gaza-1997",
            paint: {
              "line-color": "#00bfff",
              "line-width": 2,
            },
          });
        }
      }
    }
  };

  const handleRightOpen = (group) => {
    setRightOpenGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  const handleRightSelectAll = () => {
    const newAllSelected = !rightAllSelected;
    setRightAllSelected(newAllSelected);

    // Update each group's state and map layers
    Object.keys(communityGroups).forEach((code) => {
      handleRightCheck(code, newAllSelected);
    });
  };



  // Add speed control handlers
  const handleSpeedChange = (delta) => {
    setFps((prevFps) => {
      const newFps = Math.max(1, Math.min(30, prevFps + delta));
      return newFps;
    });
  };

  useEffect(() => {
    if (mapRef.current) {
      setTimeout(() => {
        mapRef.current.resize();
      }, 350); // Wait for the transition to finish
    }
  }, [showLeftLegend, showRightLegend]);

  const handleStepForward = () => {
    const newPoint1 = Math.min(point1 + 1, point2, loopEnd - 1);
    const newPoint2 = Math.min(point2 + 1, loopEnd);
    if (newPoint1 !== point1 || newPoint2 !== point2) {
      setPoint1(newPoint1);
      setPoint2(newPoint2);
    }
  };

  const handleStepBackward = () => {
    const newPoint1 = Math.max(point1 - 1, loopStart);
    const newPoint2 = Math.max(point2 - 1, point1, loopStart + 1);
    if (newPoint1 !== point1 || newPoint2 !== point2) {
      setPoint1(newPoint1);
      setPoint2(newPoint2);
    }
  };

  const handleUpdateAllThePoints = () => {
    dispatch(updateAllThePointsAction());
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden", // Prevent body scrolling
      }}
    >
      <style>{rangeStyles + playPauseStyles + speedControlStyles}</style>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "row",
          overflow: "hidden", // Prevent content from overflowing
          position: "relative",
        }}
      >
        {/* Left Legend Panel */}
        <div
          style={{
            width: showLeftLegend ? 400 : 0,
            minWidth: showLeftLegend ? 150 : 0,
            transition: "width 0.3s",
            overflow: "hidden",
            position: "relative",
            zIndex: 20,
            background: "#232323",
            boxShadow: showLeftLegend ? "2px 0 8px #0002" : "none",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {showLeftLegend && (
            <>
              <div
                className="flex-3 flex flex-col p-2"
                style={{
                  width: "400px",
                  minWidth: "150px",
                  height: "calc(100vh - 120px)",
                  overflowY: "auto",
                  position: "relative",
                  zIndex: 1,
                  background: "#232323",
                }}
              >
                <div className="flex flex-col gap-3 p-3 pb-0 sticky">
                  <div style={{ width: '100%', background: '#000', padding: '16px 0', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <img src={ATPHeading} alt="All the Points Heading" style={{ maxWidth: '320px', width: '100%', height: 'auto', objectFit: 'contain', display: 'block' }} />
                  </div>
                  <h1 className="font-bold pb-2">Legend</h1>
                  <div className="flex flex-row items-center gap-3">
                    <input
                      id="mode-toggle"
                      type="checkbox"
                      checked={advancedMode}
                      onChange={handleModeChange}
                    ></input>
                    <label htmlFor="mode-toggle" className="text-m">
                      Display subcommunities
                    </label>
                  </div>
                  <hr />
                  <div className="flex flex-row justify-between">
                    <div className="flex flex-row items-center gap-3 py-1">
                      <input
                        id="select-all"
                        type="checkbox"
                        checked={allSelected}
                        onChange={handleSelectAll}
                      ></input>
                      <label htmlFor="select-all" className="text-sm">
                        Select all
                      </label>
                    </div>
                    {advancedMode &&
                      (allExpanded ? (
                        <button
                          className="bg-gray-200 text-gray-800 rounded-md p-1 pl-3 text-sm flex"
                          onClick={handleExpandAll}
                          style={{ width: "9em" }}
                        >
                          <span className="font-bold">-</span>
                          <span className="text-center grow">Collapse All</span>
                        </button>
                      ) : (
                        <button
                          className="bg-gray-200 text-gray-800 rounded-md p-1 pl-3 text-sm flex"
                          onClick={handleExpandAll}
                          style={{ width: "9em" }}
                        >
                          <span className="font-bold">+</span>
                          <span className="text-center grow">Expand All</span>
                        </button>
                      ))}
                  </div>
                  <hr />
                </div>
                <div className="flex flex-col gap-3 p-3 overflow-scroll">
                  {Object.keys(communityGroups).map(function (groupName, i) {
                    return (
                      <div key={i}>
                        <LegendGroup
                          group={groupName}
                          iconImports={iconImports}
                          isChecked={checkedGroups[groupName]}
                          setIsChecked={handleCheck}
                          isOpen={openGroups[groupName]}
                          toggleOpen={handleOpen}
                          advancedMode={advancedMode}
                          allThePoints={allThePoints}
                          groupData={communityGroups[groupName]}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
        {/* Collapse Handle for Left Legend (outside panel) */}
        {showLeftLegend && (
          <div
            onClick={() => setShowLeftLegend(false)}
            style={{
              position: "absolute",
              top: "50%",
              left: showLeftLegend ? 400 : 0,
              width: 40,
              height: 60,
              background: "#232323",
              borderRadius: "0 20px 20px 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 25,
              color: "white",
              fontSize: "1.5em",
              boxShadow: "2px 0 8px #0002",
              transform: "translateY(-50%)",
            }}
            title="Hide legend"
          >
            {"<"}
          </div>
        )}
        {/* Expand Button for Left Legend */}
        {!showLeftLegend && (
          <div
            onClick={() => setShowLeftLegend(true)}
            style={{
              position: "absolute",
              top: "50%",
              left: 0,
              width: 60,
              height: 120,
              background: "#232323",
              borderRadius: "0 30px 30px 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 30,
              color: "white",
              fontSize: "2.5em",
              boxShadow: "2px 0 8px #0002",
              transform: "translateY(-50%)",
            }}
            title="Show legend"
          >
            {">"}
          </div>
        )}
        {/* Map Section */}
        <div
          style={{
            flex: 7,
            position: "relative",
            height: "calc(100vh - 120px)",
            zIndex: 0,
          }}
        >
          {/* Loading indicator */}
          {isDataLoading && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 10,
                background: "rgba(0, 0, 0, 0.8)",
                color: "white",
                padding: "20px",
                borderRadius: "8px",
                fontSize: "16px",
              }}
            >
              Loading map data...
            </div>
          )}
          
          {/* Error indicator */}
          {dataError && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 10,
                background: "rgba(255, 0, 0, 0.8)",
                color: "white",
                padding: "20px",
                borderRadius: "8px",
                fontSize: "16px",
              }}
            >
              Error loading data: {dataError}
            </div>
          )}

          {/* Debug panel - remove in production */}
          {process.env.NODE_ENV === 'development' && (
            <div
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                zIndex: 10,
                background: "rgba(0, 0, 0, 0.8)",
                color: "white",
                padding: "10px",
                borderRadius: "4px",
                fontSize: "12px",
                maxWidth: "300px",
              }}
            >
              <div>Data Status: {isDataLoading ? "Loading" : allThePoints ? "Loaded" : "No Data"}</div>
              <div>Google Sheet # of rows: {allThePoints ? allThePoints.length : 0}</div>
              <div>Imported # of points: {enrichedGeojson.features.length}</div>
              {dataError && <div style={{color: "red"}}>Error: {dataError}</div>}
            </div>
          )}

          <button
            onClick={() => setUnlimitedZoom(!unlimitedZoom)}
            style={{
              position: "absolute",
              top: 10,
              left: 10,
              zIndex: 1,
              padding: "8px",
              backgroundColor: unlimitedZoom ? "#e0e0e0" : "#bdbdbd",
              color: "#333",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              width: "52px",
              height: "52px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title={
              unlimitedZoom ? "Disable Unlimited Zoom" : "Enable Unlimited Zoom"
            }
          >
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ transform: unlimitedZoom ? "scale(0.8)" : "scale(1)" }}
            >
              <path d="M 7 7 L 7 10 M 7 7 L 10 7" />
              <path d="M 17 7 L 17 10 M 17 7 L 14 7" />
              <path d="M 7 17 L 7 14 M 7 17 L 10 17" />
              <path d="M 17 17 L 17 14 M 17 17 L 14 17" />
            </svg>
          </button>

          {/* Update Button */}
          <button
            onClick={handleUpdateAllThePoints}
            style={{
              position: "absolute",
              top: 10,
              left: 70,
              zIndex: 1,
              padding: "8px",
              backgroundColor: "#bdbdbd",
              color: "#333",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              width: "52px",
              height: "52px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Update Data"
          >
            {/* Standard Refresh Icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 2v6h-6" />
              <path d="M3 12a9 9 0 0 1 15-7.36L21 8" />
              <path d="M21 12a9 9 0 1 1-9-9" />
            </svg>
          </button>
          <Map
            ref={mapRef}
            initialViewState={{
              longitude: 34.8,
              latitude: 32.2,
              zoom: 7,
              pitch: 0,
            }}
            mapStyle="mapbox://styles/mapbox/dark-v11"
            mapboxAccessToken={MAPBOX_TOKEN}
            antialias={true}
            onLoad={handleMapLoad}
          />
        </div>
        {/* Right Legend Panel */}
        <div
          style={{
            width: showRightLegend ? 400 : 0,
            minWidth: showRightLegend ? 150 : 0,
            transition: "width 0.3s",
            overflow: "hidden",
            position: "relative",
            zIndex: 20,
            background: "#232323",
            boxShadow: showRightLegend ? "-2px 0 8px #0002" : "none",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {showRightLegend && (
            <>
              <div
                style={{
                  flex: 3,
                  display: "flex",
                  flexDirection: "column",
                  padding: "20px",
                  position: "relative",
                  background: "#232323",
                  width: "100%",
                  maxHeight: "calc(100vh - 120px)",
                  boxShadow: showRightLegend ? "-2px 0 8px #0002" : "none",
                }}
              >
                {/* Info panel (not scrollable) */}
                <div
                  id="info-panel"
                  style={{
                    marginBottom: "20px",
                    flex: "none",
                    maxHeight: isInfoPanelCollapsed ? undefined : "300px",
                    overflowY: isInfoPanelCollapsed ? undefined : "auto",
                    border: "1px solid #444",
                    borderRadius: "6px",
                    background: "#232323",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderBottom: "1px solid #444" }}>
                    <span style={{ fontWeight: 600 }}>Point Information</span>
                    <button
                      onClick={() => setIsInfoPanelCollapsed((v) => !v)}
                      style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", fontSize: "1.2em" }}
                      title={isInfoPanelCollapsed ? "Expand" : "Collapse"}
                    >
                      {isInfoPanelCollapsed ? "+" : "-"}
                    </button>
                  </div>
                  {!isInfoPanelCollapsed && (
                    <div style={{ padding: "12px" }}>
                      {selectedPoint ? (
                        <>
                          <h2>
                            {(() => {
                              const settlementClass = selectedPoint.SettlementClassification;
                              return `Type ${settlementClass}`;
                            })()}
                            : {selectedPoint.Name}
                          </h2>
                          <table style={{ textAlign: "left" }}>
                            <tr>
                              <th>Name: </th>
                              <td>{selectedPoint.Name}</td>
                            </tr>
                            <tr>
                              <th>Alternate Spelling(s): </th>
                              <td>{selectedPoint["Alternate Spelling(s)"] || "N/A"}</td>
                            </tr>
                            <tr>
                              <th>Creation Date: </th>
                              <td>{selectedPoint["CreationDate"] || "N/A"}</td>
                            </tr>
                            <tr>
                              <th>End Date: </th>
                              <td>{selectedPoint["EndDate"] || "N/A"}</td>
                            </tr>
                            <tr>
                              <th>Settlement Classification: </th>
                              <td>{selectedPoint["SettlementClassification"] || "N/A"}</td>
                            </tr>
                            <tr>
                              <th>District: </th>
                              <td>{selectedPoint["District"] || "N/A"}</td>
                            </tr>
                            <tr>
                              <th>Source: </th>
                              <td>{selectedPoint["Source"] || "N/A"}</td>
                            </tr>
                            <tr>
                              <th>Notes: </th>
                              <td>{selectedPoint["Notes"] || "N/A"}</td>
                            </tr>
                            {selectedPoint["Summary"] && (
                              <tr>
                                <th>Summary: </th>
                                <td style={{ maxWidth: "300px", wordWrap: "break-word" }}>
                                  {selectedPoint["Summary"]}
                                </td>
                              </tr>
                            )}
                          </table>
                        </>
                      ) : (
                        "Click a point to see details here"
                      )}
                    </div>
                  )}
                </div>
                {/* Legend section (scrollable) */}
                <div style={{ flex: 1, overflowY: "auto", border: "1px solid #444", borderRadius: "6px", background: "#232323" }}>
                  <div style={{ display: "flex", alignItems: "center", padding: "8px 12px", borderBottom: "1px solid #444" }}>
                    <span style={{ fontWeight: 600 }}>Base Map Legend</span>
                  </div>
                  <div>
                    <div
                      className="flex flex-col gap-3 p-3 pb-0 sticky"
                      style={{ background: "#232323", zIndex: 1 }}
                    >
                      <h1 className="font-bold pb-2">Base Map</h1>
                      <hr />
                      <div className="flex flex-row justify-between">
                        <div className="flex flex-row items-center gap-3 py-1">
                          <input
                            id="select-all-right"
                            type="checkbox"
                            checked={rightAllSelected}
                            onChange={handleRightSelectAll}
                          ></input>
                          <label htmlFor="select-all-right" className="text-sm">
                            Select all
                          </label>
                        </div>
                      </div>
                      <hr />
                    </div>
                    <div className="flex flex-col gap-3 p-3 overflow-scroll">
                      {(() => {
                        // Only show Peel Commission 1937 and UN Partition 1947 (remove Ma'abara/Transit Camp)
                        const baseMapKeys = ["A", "C"];
                        const result = [];
                        for (let i = 0; i < baseMapKeys.length; i++) {
                          const g = baseMapKeys[i];
                          result.push(
                            <div key={g}>
                              <BaseMap
                                group={g}
                                iconImports={iconImports}
                                isChecked={rightCheckedGroups[g]}
                                setIsChecked={handleRightCheck}
                                isOpen={rightOpenGroups[g]}
                                toggleOpen={handleRightOpen}
                                advancedMode={true}
                              />
                            </div>
                          );
                        }
                        return result;
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        {/* Collapse Handle for Right Legend (outside panel) */}
        {showRightLegend && (
          <div
            onClick={() => setShowRightLegend(false)}
            style={{
              position: "absolute",
              top: "50%",
              right: showRightLegend ? 400 : 0,
              width: 40,
              height: 60,
              background: "#232323",
              borderRadius: "20px 0 0 20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 25,
              color: "white",
              fontSize: "1.5em",
              boxShadow: "-2px 0 8px #0002",
              transform: "translateY(-50%)",
            }}
            title="Hide legend"
          >
            {">"}
          </div>
        )}
        {/* Expand Button for Right Legend */}
        {!showRightLegend && (
          <div
            onClick={() => setShowRightLegend(true)}
            style={{
              position: "absolute",
              top: "50%",
              right: 0,
              width: 60,
              height: 120,
              background: "#232323",
              borderRadius: "30px 0 0 30px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 30,
              color: "white",
              fontSize: "2.5em",
              boxShadow: "-2px 0 8px #0002",
              transform: "translateY(-50%)",
            }}
            title="Show legend"
          >
            {"<"}
          </div>
        )}
      </div>

      {/* Timeline Control */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          background: "#111", // Pure black background
          borderTop: "2px solid #333", // Only top border
          zIndex: 1000,
          height: "140px",
          boxShadow: "none", // Remove shadow
          padding: 0, // Remove extra padding
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "1800px", // Limit content width for large screens
            margin: "0 auto", // Center content
            padding: "0 40px", // Margin from left and right
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "none", // Remove inner background
              padding: 0,
              position: "relative",
              border: "none", // Remove border
              borderRadius: 0,
              width: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                marginBottom: "20px",
                width: "100%",
              }}
            >
              <div className="timeline-container" style={{ width: "100%" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    position: "absolute",
                    left: "50%",
                    top: "-50px",
                    transform: "translateX(-50%)",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: "10px" }}
                  >
                    <button
                      className="speed-button"
                      onClick={handleStepBackward}
                      title="Step backward"
                      style={{
                        position: "static",
                        left: "unset",
                        top: "unset",
                        transform: "none",
                      }}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="24"
                        height="24"
                        fill="white"
                      >
                        <path d="M15 18l-6-6 6-6" />
                      </svg>
                    </button>
                    <div className="speed-display">YEARS</div>
                    <button
                      className="speed-button"
                      onClick={handleStepForward}
                      title="Step forward"
                      style={{
                        position: "static",
                        left: "unset",
                        top: "unset",
                        transform: "none",
                        marginRight: "20px",
                      }}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="24"
                        height="24"
                        fill="white"
                      >
                        <path d="M9 18l6-6-6-6" />
                      </svg>
                    </button>
                  </div>
                  <button
                    className="play-pause-button"
                    onClick={togglePlayPause}
                    title={isPlaying ? "Pause" : "Play"}
                    style={{
                      position: "static",
                      left: "unset",
                      top: "unset",
                      transform: "none",
                      margin: "0 20px",
                    }}
                  >
                    {isPlaying ? (
                      <svg viewBox="0 0 24 24">
                        <rect x="6" y="4" width="4" height="16" />
                        <rect x="14" y="4" width="4" height="16" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24">
                        <path d="M8,5 L19,12 L8,19 Z" />
                      </svg>
                    )}
                  </button>
                  <div className="speed-controls">
                    <button
                      className="speed-button"
                      onClick={() => handleSpeedChange(-1)}
                      title="Decrease speed"
                    >
                      -
                    </button>
                    <div className="speed-display">{fps} FPS</div>
                    <button
                      className="speed-button"
                      onClick={() => handleSpeedChange(1)}
                      title="Increase speed"
                    >
                      +
                    </button>
                  </div>
                </div>
                <div
                  className="timeline-track"
                  onMouseDown={handleTrackDrag}
                  style={{ position: "relative", zIndex: 0, width: "100%" }} // Ensure full width
                ></div>
                <button
                  className={`timeline-endpoint${
                    loopStart === point1 ? " selected" : ""
                  }`}
                  style={{
                    left: `calc(${(loopStart - MIN_YEAR) / (MAX_YEAR - MIN_YEAR)} * (100% - 24px))`,
                  }}
                  onMouseDown={(e) => {
                    const container = e.target.parentElement;
                    const rect = container.getBoundingClientRect();
                    const trackWidth = rect.width - 24;

                    const handleMouseMove = (e) => {
                      const position = (e.clientX - rect.left) / trackWidth;
                      const clampedPosition = Math.max(0, Math.min(1, position));
                      let newValue = Math.round(
                        MIN_YEAR + clampedPosition * (MAX_YEAR - MIN_YEAR)
                      );
                      // Outer point cannot cross point1
                      newValue = Math.min(newValue, point1 - 1);
                      newValue = Math.max(MIN_YEAR, newValue);
                      setLoopStart(newValue);
                      // If point1 is now outside, move it in
                      if (point1 <= newValue) setPoint1(newValue + 1);
                    };

                    const handleMouseUp = () => {
                      document.removeEventListener("mousemove", handleMouseMove);
                      document.removeEventListener("mouseup", handleMouseUp);
                    };

                    document.addEventListener("mousemove", handleMouseMove);
                    document.addEventListener("mouseup", handleMouseUp);
                  }}
                >
                  <span
                    style={{
                      fontSize: 40,
                      color: "#403c3c",
                      fontFamily: "serif",
                      fontWeight: "bold",
                      lineHeight: "40px",
                      pointerEvents: "none",
                      marginLeft: "-10px",
                      marginBottom: "4px",
                    }}
                  >
                    ●
                  </span>
                  <div className="timeline-endpoint-label">{loopStart}</div>
                </button>
                <button
                  className={`timeline-endpoint${
                    loopEnd === point2 ? " selected" : ""
                  }`}
                  style={{
                    left: `calc(${(loopEnd - MIN_YEAR) / (MAX_YEAR - MIN_YEAR)} * (100% - 24px))`,
                  }}
                  onMouseDown={(e) => {
                    const container = e.target.parentElement;
                    const rect = container.getBoundingClientRect();
                    const trackWidth = rect.width - 24;

                    const handleMouseMove = (e) => {
                      const position = (e.clientX - rect.left) / trackWidth;
                      const clampedPosition = Math.max(0, Math.min(1, position));
                      let newValue = Math.round(
                        MIN_YEAR + clampedPosition * (MAX_YEAR - MIN_YEAR)
                      );
                      // Outer point cannot cross point2
                      newValue = Math.max(newValue, point2 + 1);
                      newValue = Math.min(MAX_YEAR, newValue);
                      setLoopEnd(newValue);
                      // If point2 is now outside, move it in
                      if (point2 >= newValue) setPoint2(newValue - 1);
                    };

                    const handleMouseUp = () => {
                      document.removeEventListener("mousemove", handleMouseMove);
                      document.removeEventListener("mouseup", handleMouseUp);
                    };

                    document.addEventListener("mousemove", handleMouseMove);
                    document.addEventListener("mouseup", handleMouseUp);
                  }}
                >
                  <span
                    style={{
                      fontSize: 40,
                      color: "#403c3c",
                      fontFamily: "serif",
                      fontWeight: "bold",
                      lineHeight: "40px",
                      pointerEvents: "none",
                      marginBottom: "4px",
                    }}
                  >
                    ●
                  </span>
                  <div className="timeline-endpoint-label">{loopEnd}</div>
                </button>
                <div
                  className="timeline-point"
                  style={{
                    "--position": (point1 - MIN_YEAR) / (MAX_YEAR - MIN_YEAR),
                    left: `calc(var(--position) * (100% - 24px))`,
                  }}
                  onMouseDown={(e) => {
                    const container = e.target.parentElement;
                    const rect = container.getBoundingClientRect();
                    const trackWidth = rect.width - 24;

                    const handleMouseMove = (e) => {
                      const position = (e.clientX - rect.left) / trackWidth;
                      const clampedPosition = Math.max(0, Math.min(1, position));
                      let newValue = Math.round(
                        MIN_YEAR + clampedPosition * (MAX_YEAR - MIN_YEAR)
                      );
                      // Inner point cannot come closer than 5 years to point2, and must be after loopStart
                      newValue = Math.max(
                        loopStart + 1,
                        Math.min(newValue, point2 - 1)
                      );
                      if (newValue !== point1) {
                        setPoint1(newValue);
                      }
                    };

                    const handleMouseUp = () => {
                      document.removeEventListener("mousemove", handleMouseMove);
                      document.removeEventListener("mouseup", handleMouseUp);
                      // Clamp to valid range
                      setPoint1((prev) =>
                        Math.max(loopStart + 1, Math.min(prev, point2 - 1))
                      );
                    };

                    document.addEventListener("mousemove", handleMouseMove);
                    document.addEventListener("mouseup", handleMouseUp);
                  }}
                >
                  <span
                    style={{
                      fontSize: 40,
                      color: "#fff",
                      fontFamily: "serif",
                      fontWeight: "bold",
                      lineHeight: "40px",
                      pointerEvents: "none",
                      marginBottom: "4px",
                    }}
                  >
                    ●
                  </span>
                  <div
                    className="timeline-point-label"
                    ref={point1LabelRef}
                    style={labelsOverlap ? { top: "-32px" } : {}}
                  >
                    {point1}
                  </div>
                </div>
                <div
                  className="timeline-point"
                  style={{
                    "--position": (point2 - MIN_YEAR) / (MAX_YEAR - MIN_YEAR),
                    left: `calc(var(--position) * (100% - 24px))`,
                  }}
                  onMouseDown={(e) => {
                    const container = e.target.parentElement;
                    const rect = container.getBoundingClientRect();
                    const trackWidth = rect.width - 24;

                    const handleMouseMove = (e) => {
                      const position = (e.clientX - rect.left) / trackWidth;
                      const clampedPosition = Math.max(0, Math.min(1, position));
                      let newValue = Math.round(
                        MIN_YEAR + clampedPosition * (MAX_YEAR - MIN_YEAR)
                      );
                      // Inner point cannot come closer than 5 years to point1, and must be before loopEnd
                      newValue = Math.max(
                        point1 + 1,
                        Math.min(newValue, loopEnd - 1)
                      );
                      if (newValue !== point2) {
                        setPoint2(newValue);
                      }
                    };

                    const handleMouseUp = () => {
                      document.removeEventListener("mousemove", handleMouseMove);
                      document.removeEventListener("mouseup", handleMouseUp);
                      // Clamp to valid range
                      setPoint2((prev) =>
                        Math.max(point1 + 1, Math.min(prev, loopEnd - 1))
                      );
                    };

                    document.addEventListener("mousemove", handleMouseMove);
                    document.addEventListener("mouseup", handleMouseUp);
                  }}
                >
                  <span
                    style={{
                      fontSize: 40,
                      color: "#fff",
                      fontFamily: "serif",
                      fontWeight: "bold",
                      lineHeight: "40px",
                      pointerEvents: "none",
                      marginBottom: "4px",
                    }}
                  >
                    ●
                  </span>
                  <div
                    className="timeline-point-label"
                    ref={point2LabelRef}
                    style={labelsOverlap ? { top: "24px" } : {}}
                  >
                    {point2}
                  </div>
                </div>
                <div className="timeline-labels">
                  {Array.from({ length: 19 }, (_, i) => {
                    const year = MIN_YEAR + i * 10 + 1; // Add 1 to start from 1840
                    if (year <= MAX_YEAR) {
                      const position = (year - MIN_YEAR) / (MAX_YEAR - MIN_YEAR);
                      return (
                        <div
                          key={year}
                          className="timeline-label"
                          style={{
                            left: `${position * 100}%`,
                            transform:
                              "translateX(-50%)" /* Ensure consistent centering */,
                          }}
                        >
                          {year}
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
